import { buildCliLaunchInvocation, buildProviderExitError, classifyCliDriverTimeoutFailure, classifyRuntimePermissionDenialMessage, looksLikeRuntimePermissionDenialMessage, } from "@wakeloop/adapter-core";
import { CliDriverError, runCliDriver, } from "@wakeloop/adapter-core/drivers/cli-driver";
import { processCodexStdoutLine } from "../output/codex-output-parser.js";
export class CodexCliTransportError extends Error {
    code;
    errorType;
    constructor(input) {
        super(input.message);
        this.name = "CodexCliTransportError";
        this.code = input.code;
        this.errorType = input.errorType;
    }
}
export async function invokeCodexCliTransport(input) {
    const prompt = normalizeOptionalText(input.prompt);
    if (!prompt) {
        throw new CodexCliTransportError({
            code: "schema.invalid",
            errorType: "config",
            message: "schema.invalid: prompt is required",
        });
    }
    const args = buildCodexExecArgs({
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        conversationPolicy: input.conversationPolicy,
        executionMode: input.executionMode,
        threadId: input.threadId,
    });
    const invocation = buildCliLaunchInvocation({
        args,
        launchContract: input.launchContract,
    });
    const invokedAtMs = Date.now();
    let latestOutput = "";
    let resolvedThreadId = input.threadId;
    const providerErrorMessages = [];
    let firstStdoutAtMs = null;
    let firstOutputAtMs = null;
    const previewEmitter = createPreviewEmitter({
        onPreview: input.onPreview,
        streamingMode: input.streamingMode,
    });
    let driverResult;
    try {
        driverResult = await runCliDriver({
            allowNonZeroExit: true,
            args: invocation.args,
            bin: invocation.bin,
            cwd: input.codexWorkdir,
            env: input.env,
            inputText: `${prompt}\n`,
            onLifecycleEvent: input.onRuntimeEvent,
            onProcessControlEvent: input.onProcessControlEvent,
            onStdoutLine: (event) => {
                if (firstStdoutAtMs === null) {
                    firstStdoutAtMs = Date.now();
                }
                const previousOutput = latestOutput;
                const nextLineResult = processCodexStdoutLine({
                    latestOutputRef: {
                        get value() {
                            return latestOutput;
                        },
                        set value(value) {
                            latestOutput = value;
                        },
                    },
                    line: event.line,
                    threadIdRef: {
                        get value() {
                            return resolvedThreadId;
                        },
                        set value(value) {
                            resolvedThreadId = value;
                        },
                    },
                });
                if (firstOutputAtMs === null &&
                    previousOutput.trim().length === 0 &&
                    latestOutput.trim().length > 0) {
                    firstOutputAtMs = Date.now();
                }
                if (nextLineResult.previewable && latestOutput !== previousOutput) {
                    previewEmitter.push(latestOutput);
                }
                collectCodexErrorMessage(event.line, providerErrorMessages);
                if (nextLineResult.terminal) {
                    return {
                        complete: true,
                        terminateProcess: true,
                    };
                }
            },
            onStderrLine: (event) => {
                collectCodexErrorMessage(event.line, providerErrorMessages);
            },
            timeoutMs: input.codexTimeoutMs,
        });
    }
    catch (error) {
        throw toCodexCliTransportError(error, input.codexTimeoutMs);
    }
    await previewEmitter.flush();
    if (driverResult.exitCode !== 0) {
        const classifiedFailure = classifyCodexFailure({
            providerErrorMessages,
            stderrText: driverResult.stderrText,
            stdoutText: driverResult.stdoutText,
        });
        throw new CodexCliTransportError({
            code: classifiedFailure.code,
            errorType: classifiedFailure.errorType,
            message: classifiedFailure.message,
        });
    }
    const output = normalizeOptionalText(latestOutput);
    if (!output) {
        if (providerErrorMessages.length > 0) {
            const classifiedFailure = classifyCodexFailure({
                providerErrorMessages,
                stderrText: driverResult.stderrText,
                stdoutText: driverResult.stdoutText,
            });
            throw new CodexCliTransportError({
                code: classifiedFailure.code,
                errorType: classifiedFailure.errorType,
                message: classifiedFailure.message,
            });
        }
        throw new CodexCliTransportError({
            code: "runtime.output.empty",
            errorType: "adapter",
            message: "runtime.output.empty: codex finished without agent output",
        });
    }
    return {
        output,
        telemetry: {
            completedAtMs: Date.now(),
            ...(driverResult.lifecycle.exitCode === null
                ? {}
                : { exitCode: driverResult.lifecycle.exitCode }),
            ...(driverResult.lifecycle.exitObservedAtMs === null
                ? {}
                : { exitObservedAtMs: driverResult.lifecycle.exitObservedAtMs }),
            ...(driverResult.lifecycle.exitSignal === null
                ? {}
                : { exitSignal: driverResult.lifecycle.exitSignal }),
            ...(firstOutputAtMs === null ? {} : { firstOutputAtMs }),
            ...(driverResult.lifecycle.firstStderrAtMs === null
                ? {}
                : { firstStderrAtMs: driverResult.lifecycle.firstStderrAtMs }),
            ...(firstStdoutAtMs === null ? {} : { firstStdoutAtMs }),
            invokedAtMs,
            previewEmitted: previewEmitter.emitted,
            ...(driverResult.lifecycle.processId === null
                ? {}
                : { processId: driverResult.lifecycle.processId }),
            ...(driverResult.lifecycle.processSpawnedAtMs === null
                ? {}
                : { processSpawnedAtMs: driverResult.lifecycle.processSpawnedAtMs }),
            resumedContext: normalizeOptionalText(input.threadId) !== null,
            ...(driverResult.lifecycle.stdinClosedAtMs === null
                ? {}
                : { stdinClosedAtMs: driverResult.lifecycle.stdinClosedAtMs }),
            streamingMode: input.streamingMode ?? "final",
            ...(driverResult.lifecycle.timeoutTriggeredAtMs === null
                ? {}
                : {
                    timeoutTriggeredAtMs: driverResult.lifecycle.timeoutTriggeredAtMs,
                }),
        },
        threadId: normalizeOptionalText(resolvedThreadId),
    };
}
export function buildCodexExecArgs(input) {
    const conversationPolicyArgs = buildCodexConversationPolicyArgs(input.conversationPolicy);
    const permissionModeArgs = resolveCodexAgentControllerPermissionModeArgs({
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        executionMode: input.executionMode,
    });
    if (input.threadId) {
        return [
            ...permissionModeArgs.globalArgs,
            "exec",
            "resume",
            ...permissionModeArgs.execArgs,
            "--json",
            "--skip-git-repo-check",
            ...conversationPolicyArgs,
            input.threadId,
            "-",
        ];
    }
    return [
        ...permissionModeArgs.globalArgs,
        "exec",
        ...permissionModeArgs.execArgs,
        "--json",
        "--skip-git-repo-check",
        ...conversationPolicyArgs,
        "-",
    ];
}
function resolveCodexAgentControllerPermissionModeArgs(input) {
    if (input.executionMode === "repair") {
        return {
            execArgs: [],
            globalArgs: ["-s", "read-only", "-a", "untrusted"],
        };
    }
    const policy = resolveCodexAgentControllerPermissionMode(input.agentControllerPermissionMode);
    if (policy === "codex:fullAccess") {
        return {
            execArgs: ["--dangerously-bypass-approvals-and-sandbox"],
            globalArgs: [],
        };
    }
    if (policy === "codex:customConfig") {
        return {
            execArgs: [],
            globalArgs: [],
        };
    }
    const mapping = CODEX_CLI_POLICY_ARGS_BY_POLICY[policy];
    return {
        execArgs: [],
        globalArgs: [
            "-s",
            mapping.sandbox,
            "-a",
            mapping.askForApproval,
            ...mapping.configArgs,
        ],
    };
}
const CODEX_CLI_POLICY_ARGS_BY_POLICY = {
    "codex:askForApproval": {
        askForApproval: "untrusted",
        configArgs: [],
        sandbox: "workspace-write",
    },
    "codex:approveForMe": {
        askForApproval: "on-request",
        configArgs: ["-c", 'approvals_reviewer="auto_review"'],
        sandbox: "workspace-write",
    },
};
function resolveCodexAgentControllerPermissionMode(value) {
    switch (value) {
        case "codex:approveForMe":
        case "codex:fullAccess":
        case "codex:customConfig":
            return value;
        default:
            return "codex:askForApproval";
    }
}
function buildCodexConversationPolicyArgs(conversationPolicy) {
    const args = [];
    const model = normalizeOptionalText(conversationPolicy?.model);
    if (model) {
        args.push("-m", model);
    }
    const reasoningLevel = normalizeOptionalText(conversationPolicy?.reasoningLevel);
    if (reasoningLevel) {
        args.push("-c", `model_reasoning_effort=${reasoningLevel}`);
    }
    return args;
}
const PREVIEW_EMIT_MIN_VISIBLE_CHARS = 24;
const PREVIEW_EMIT_THROTTLE_MS = 500;
const createPreviewEmitter = (input) => {
    if (!(input.onPreview && input.streamingMode === "stream")) {
        return {
            push: () => undefined,
            emitted: false,
            flush: async () => undefined,
        };
    }
    let emitted = false;
    let lastEmittedAtMs = 0;
    let lastEmittedText = "";
    let latestPreviewText = "";
    const pendingTasks = new Set();
    const queuePreview = (nextPreviewText) => {
        emitted = true;
        lastEmittedAtMs = Date.now();
        lastEmittedText = nextPreviewText;
        const task = Promise.resolve(input.onPreview?.({
            text: nextPreviewText,
        }))
            .catch(() => undefined)
            .finally(() => {
            pendingTasks.delete(task);
        });
        pendingTasks.add(task);
    };
    return {
        push: (nextPreviewText) => {
            const normalizedPreviewText = nextPreviewText.trim();
            if (!normalizedPreviewText ||
                normalizedPreviewText === latestPreviewText) {
                return;
            }
            latestPreviewText = normalizedPreviewText;
            if (normalizedPreviewText === lastEmittedText) {
                return;
            }
            const nowMs = Date.now();
            const grewEnough = normalizedPreviewText.length - lastEmittedText.length >=
                PREVIEW_EMIT_MIN_VISIBLE_CHARS;
            const addedNewLineBoundary = countVisibleLines(normalizedPreviewText) >
                countVisibleLines(lastEmittedText);
            if (lastEmittedText &&
                nowMs - lastEmittedAtMs < PREVIEW_EMIT_THROTTLE_MS) {
                return;
            }
            if (!(lastEmittedText === "" || grewEnough || addedNewLineBoundary)) {
                return;
            }
            queuePreview(normalizedPreviewText);
        },
        get emitted() {
            return emitted;
        },
        flush: async () => {
            if (latestPreviewText && latestPreviewText !== lastEmittedText) {
                queuePreview(latestPreviewText);
            }
            await Promise.allSettled([...pendingTasks]);
        },
    };
};
const countVisibleLines = (value) => {
    if (value.length === 0) {
        return 0;
    }
    return value.split("\n").length;
};
const toCodexCliTransportError = (error, timeoutMs) => {
    if (error instanceof CodexCliTransportError) {
        return error;
    }
    if (error instanceof CliDriverError) {
        if (error.code === "process.timeout") {
            const classifiedFailure = classifyCliDriverTimeoutFailure({
                error,
                providerLabel: "codex",
                timeoutMs,
            });
            return new CodexCliTransportError(classifiedFailure);
        }
        if (error.code === "process.spawn_failed") {
            return new CodexCliTransportError({
                code: "upstream_unreachable",
                errorType: "adapter",
                message: `upstream_unreachable: failed to spawn codex (${error.message})`,
            });
        }
        if (error.code === "process.non_zero_exit") {
            const classifiedFailure = classifyCodexFailure({
                providerErrorMessages: [],
                stderrText: error.stderrText,
                stdoutText: error.stdoutText,
            });
            return new CodexCliTransportError({
                code: classifiedFailure.code,
                errorType: classifiedFailure.errorType,
                message: classifiedFailure.message,
            });
        }
    }
    const fallbackMessage = error instanceof Error ? error.message : String(error);
    return new CodexCliTransportError({
        code: "upstream_unreachable",
        errorType: "adapter",
        message: `upstream_unreachable: failed to execute codex (${fallbackMessage})`,
    });
};
const collectCodexErrorMessage = (line, messages) => {
    const normalizedLine = normalizeOptionalText(line);
    if (!normalizedLine) {
        return;
    }
    const jsonMessage = readCodexJsonErrorMessage(normalizedLine);
    if (jsonMessage) {
        messages.push(jsonMessage);
    }
};
const readCodexJsonErrorMessage = (line) => {
    let parsed;
    try {
        parsed = JSON.parse(line);
    }
    catch {
        return null;
    }
    const record = parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    if (!record) {
        return null;
    }
    const message = normalizeOptionalText(record.message);
    if (record.type === "error" && message) {
        return message;
    }
    const errorRecord = record.error &&
        typeof record.error === "object" &&
        !Array.isArray(record.error)
        ? record.error
        : null;
    const errorMessage = normalizeOptionalText(errorRecord?.message);
    if (record.type === "turn.failed" && errorMessage) {
        return errorMessage;
    }
    return null;
};
const classifyCodexFailure = (input) => {
    const providerMessage = resolveProviderFailureMessage(input);
    if (providerMessage) {
        const lowered = providerMessage.toLowerCase();
        const permissionDenial = classifyRuntimePermissionDenialMessage({
            message: providerMessage,
            source: "codex",
        });
        if (permissionDenial) {
            return {
                code: permissionDenial.code,
                errorType: permissionDenial.errorType,
                message: permissionDenial.message,
            };
        }
        if (isControllerClientUpgradeRequiredFailure(lowered)) {
            return {
                code: "controller.client.upgrade_required",
                errorType: "config",
                message: toControllerClientUpgradeRequiredMessage(providerMessage),
            };
        }
        if (isQuotaFailure(lowered)) {
            return {
                code: "quota.exceeded",
                errorType: "adapter",
                message: providerMessage,
            };
        }
        if (isAuthFailure(lowered)) {
            return {
                code: "auth.required",
                errorType: "auth",
                message: providerMessage,
            };
        }
        if (isRateLimitFailure(lowered)) {
            return {
                code: "rate_limited",
                errorType: "adapter",
                message: providerMessage,
            };
        }
        if (isConfigFailure(lowered)) {
            return {
                code: "config.invalid",
                errorType: "config",
                message: toConfigInvalidMessage(providerMessage),
            };
        }
    }
    const stderrText = normalizeOptionalText(input.stderrText);
    if (stderrText) {
        return {
            code: "upstream_unreachable",
            errorType: "adapter",
            message: `upstream_unreachable: ${stderrText}`,
        };
    }
    return {
        ...buildProviderExitError({
            providerLabel: "codex",
        }),
    };
};
const resolveProviderFailureMessage = (input) => {
    const candidateMessages = [
        ...input.providerErrorMessages,
        ...(normalizeOptionalText(input.stderrText)
            ? [normalizeOptionalText(input.stderrText)]
            : []),
        ...(normalizeOptionalText(input.stdoutText)
            ? [normalizeOptionalText(input.stdoutText)]
            : []),
    ].filter((value) => Boolean(value));
    for (const candidate of candidateMessages) {
        if (looksLikeProviderFailure(candidate)) {
            return candidate;
        }
    }
    return candidateMessages[0] ?? null;
};
const looksLikeProviderFailure = (value) => {
    const normalized = value.toLowerCase();
    return (looksLikeRuntimePermissionDenialMessage(value) ||
        isAuthFailure(normalized) ||
        isQuotaFailure(normalized) ||
        isRateLimitFailure(normalized));
};
const isAuthFailure = (value) => {
    return (value.includes("401") ||
        value.includes("unauthorized") ||
        value.includes("missing bearer") ||
        value.includes("not logged in") ||
        value.includes("authentication"));
};
const isQuotaFailure = (value) => {
    return (value.includes("insufficient_quota") ||
        value.includes("insufficient balance") ||
        value.includes("insufficient_balance") ||
        value.includes("quota exceeded") ||
        value.includes("exceeded your current quota") ||
        value.includes("not enough credits") ||
        value.includes("out of credits") ||
        value.includes("credits exhausted") ||
        value.includes("balance is too low") ||
        value.includes("payment required") ||
        value.includes("billing"));
};
const isRateLimitFailure = (value) => {
    return (value.includes("429") ||
        value.includes("rate limit") ||
        value.includes("too many requests"));
};
const isConfigFailure = (value) => {
    return (value.includes("model_not_found") ||
        value.includes("requested model") ||
        value.includes("unknown model") ||
        value.includes("invalid model") ||
        (value.includes("does not exist") && value.includes("model")) ||
        (value.includes("invalid_request_error") && value.includes("model")) ||
        value.includes('"param": "model"'));
};
const isControllerClientUpgradeRequiredFailure = (value) => {
    return (value.includes("requires a newer version of codex") ||
        (value.includes("upgrade to the latest app or cli") &&
            value.includes("codex")));
};
const toConfigInvalidMessage = (value) => {
    return value.toLowerCase().startsWith("config.invalid:")
        ? value
        : `config.invalid: ${value}`;
};
const toControllerClientUpgradeRequiredMessage = (value) => {
    return value.toLowerCase().startsWith("controller.client.upgrade_required:")
        ? value
        : `controller.client.upgrade_required: ${value}`;
};
function normalizeText(value) {
    return String(value ?? "").trim();
}
function normalizeOptionalText(value) {
    const normalized = normalizeText(value);
    return normalized.length > 0 ? normalized : null;
}
